import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DonationService from '../../service/DonationService';
import styles from './DonationHistoryPage.module.scss';

const DonationHistoryPage = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    fetchHistory();
  }, [filterStatus]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const data = await DonationService.getDonationHistory(
        filterStatus !== 'all' ? filterStatus : null
      );
      setHistory(data);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки истории');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 100) return '#4caf50'; // Completed
    if (status === 101) return '#ff9800'; // Processing
    if (status === 102) return '#f44336'; // Refunded
    return '#9e9e9e';
  };

  const getStatusIcon = (status) => {
    if (status === 100) return '✓';
    if (status === 101) return '⏳';
    if (status === 102) return '↩️';
    return '?';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={styles.historyPage}>
        <div className={styles.loadingSpinner}>
          <div className={styles.spinner}></div>
          <p>Загрузка истории...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.historyPage}>
      <div className={styles.historyHeader}>
        <button className={styles.backButton} onClick={() => navigate('/donations')}>
          ← Назад к донатам
        </button>
        <h1>📜 История донатов</h1>
      </div>

      <div className={styles.historyFilters}>
        <button
          className={`${styles.filterBtn} ${filterStatus === 'all' ? styles.active : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          Все
        </button>
        <button
          className={`${styles.filterBtn} ${filterStatus === '100' ? styles.active : ''}`}
          onClick={() => setFilterStatus('100')}
        >
          ✓ Выполнены
        </button>
        <button
          className={`${styles.filterBtn} ${filterStatus === '101' ? styles.active : ''}`}
          onClick={() => setFilterStatus('101')}
        >
          ⏳ В обработке
        </button>
        <button
          className={`${styles.filterBtn} ${filterStatus === '102' ? styles.active : ''}`}
          onClick={() => setFilterStatus('102')}
        >
          ↩️ Возвраты
        </button>
      </div>

      {error && (
        <div className={styles.errorMessage}>
          <p>{error}</p>
          <button onClick={fetchHistory}>Попробовать снова</button>
        </div>
      )}

      {history.length === 0 ? (
        <div className={styles.noHistory}>
          <div className={styles.noHistoryIcon}>🛒</div>
          <h2>История пуста</h2>
          <p>У вас пока нет заказов донатов</p>
          <button className={styles.goShoppingBtn} onClick={() => navigate('/donations')}>
            Перейти к донатам
          </button>
        </div>
      ) : (
        <div className={styles.historyList}>
          {history.map(item => (
            <div key={item.id} className={styles.historyItem}>
              <div className={styles.historyItemHeader}>
                <div className={styles.orderInfo}>
                  <h3>{item.order.product_name}</h3>
                  <p className={styles.orderCategory}>{item.order.category_name}</p>
                  <p className={styles.orderId}>Заказ #{item.order.merchant_order_id}</p>
                </div>
                
                <div
                  className={styles.statusBadge}
                  style={{ backgroundColor: getStatusColor(item.order.status) }}
                >
                  <span className={styles.statusIcon}>{getStatusIcon(item.order.status)}</span>
                  <span>{item.order.status_text}</span>
                </div>
              </div>

              <div className={styles.historyItemBody}>
                <div className={styles.orderDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Сумма:</span>
                    <span className={styles.detailValue}>{item.order.amount} сом</span>
                  </div>
                  
                  {item.order.count > 1 && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Количество:</span>
                      <span className={styles.detailValue}>{item.order.count}</span>
                    </div>
                  )}

                  {item.order.input1 && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>ID пользователя:</span>
                      <span className={styles.detailValue}>{item.order.input1}</span>
                    </div>
                  )}

                  {item.order.input2 && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Сервер:</span>
                      <span className={styles.detailValue}>{item.order.input2}</span>
                    </div>
                  )}

                  {item.order.codes && item.order.codes.length > 0 && (
                    <div className={styles.codesSection}>
                      <span className={styles.detailLabel}>Коды:</span>
                      <div className={styles.codesList}>
                        {item.order.codes.map((code, index) => (
                          <div key={index} className={styles.codeItem}>
                            <code>{code}</code>
                            <button
                              className={styles.copyBtn}
                              onClick={() => {
                                navigator.clipboard.writeText(code);
                                alert('Код скопирован!');
                              }}
                            >
                              📋
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.orderTimeline}>
                  <div className={styles.timelineItem}>
                    <span className={styles.timelineLabel}>Создан:</span>
                    <span className={styles.timelineDate}>{formatDate(item.order.date_created)}</span>
                  </div>
                  
                  {item.order.date_completed && (
                    <div className={styles.timelineItem}>
                      <span className={styles.timelineLabel}>Завершён:</span>
                      <span className={styles.timelineDate}>{formatDate(item.order.date_completed)}</span>
                    </div>
                  )}
                </div>
              </div>

              {item.order.error_message && (
                <div className={styles.errorInfo}>
                  <strong>Ошибка:</strong> {item.order.error_message}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DonationHistoryPage;
